import { useState, useEffect } from 'react';
import { Users, Plus, Building2, Mail, User, ArrowLeft, Pencil, Trash2 } from 'lucide-react';
import axios from 'axios';
import { api, getErrorMessage } from '@/shared/services/api';
import { useAuthStore } from '@/app/store/authStore';
import toast from 'react-hot-toast';
import { ClientMatrix } from './ClientMatrix';
import { Spinner } from '@/shared/components/Spinner';
import { EmptyState } from '@/shared/components/EmptyState';
import { cn } from '@/shared/utils/cn';

interface Client {
  id: number;
  name: string;
  description?: string;
  contactEmail?: string;
  contactPerson?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

type ClientDraft = Pick<Client, 'name' | 'description' | 'contactEmail' | 'contactPerson'>;

const EMPTY_DRAFT: ClientDraft = {
  name: '',
  description: '',
  contactEmail: '',
  contactPerson: '',
};

export function Clients() {
  // SC-239 restricts maintaining the client list to admins; the server enforces this on every
  // write, so this only keeps the controls out of a non-admin's way.
  const isAdmin = useAuthStore((state) => state.user?.isAdmin ?? false);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [editDraft, setEditDraft] = useState<ClientDraft>(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [showMatrix, setShowMatrix] = useState(false);
  const [newClient, setNewClient] = useState({
    name: '',
    description: '',
    contactEmail: '',
    contactPerson: '',
  });

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      setLoading(true);
      const response = await api.get('/clients');
      setClients(response.data);
    } catch (error) {
      toast.error('Failed to load clients');
      console.error('Error loading clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddClient = async () => {
    if (!newClient.name.trim()) {
      toast.error('Client name is required');
      return;
    }

    try {
      const response = await api.post('/clients', newClient);
      setClients([...clients, response.data]);
      setShowAddModal(false);
      setNewClient({ name: '', description: '', contactEmail: '', contactPerson: '' });
      toast.success('Client added successfully');
    } catch (error) {
      const msg = axios.isAxiosError(error) ? (error.response?.data as string) : null;
      toast.error(msg || 'Failed to add client');
      console.error('Error adding client:', error);
    }
  };

  const startEdit = (client: Client) => {
    setEditingClient(client);
    setEditDraft({
      name: client.name,
      description: client.description ?? '',
      contactEmail: client.contactEmail ?? '',
      contactPerson: client.contactPerson ?? '',
    });
  };

  const cancelEdit = () => {
    setEditingClient(null);
    setEditDraft(EMPTY_DRAFT);
  };

  const handleUpdateClient = async () => {
    if (!editingClient) return;

    if (!editDraft.name.trim()) {
      toast.error('Client name is required');
      return;
    }

    setSaving(true);
    try {
      await api.put(`/clients/${editingClient.id}`, editDraft);
      setClients((prev) =>
        prev.map((c) => (c.id === editingClient.id ? { ...c, ...editDraft } : c))
      );
      cancelEdit();
      toast.success('Client updated');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update client'));
      console.error('Error updating client:', error);
    } finally {
      setSaving(false);
    }
  };

  // Removal is permanent, so it is confirmed. Deactivation stays the reversible option and is
  // the better choice for a client that merely became dormant.
  const handleDeleteClient = async (client: Client) => {
    const confirmed = window.confirm(
      `Remove "${client.name}" from the client list? This cannot be undone — deactivate instead if you only want to hide it.`
    );
    if (!confirmed) return;

    setSaving(true);
    try {
      await api.delete(`/clients/${client.id}`);
      setClients((prev) => prev.filter((c) => c.id !== client.id));
      toast.success('Client removed');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to remove client'));
      console.error('Error removing client:', error);
    } finally {
      setSaving(false);
    }
  };

  const toggleClientStatus = async (client: Client) => {
    try {
      const endpoint = client.isActive ? 'deactivate' : 'activate';
      await api.post(`/clients/${client.id}/${endpoint}`);

      setClients(
        clients.map((c) =>
          c.id === client.id ? { ...c, isActive: !c.isActive } : c
        )
      );
      toast.success(`Client ${client.isActive ? 'deactivated' : 'activated'}`);
    } catch (error) {
      toast.error('Failed to update client status');
      console.error('Error updating client status:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner className="h-8 w-8 text-primary" />
      </div>
    );
  }

  if (showMatrix) {
    return (
      <div className="animate-fade-in">
        <button
          onClick={() => setShowMatrix(false)}
          className="btn-ghost btn-sm mb-4 gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Clients
        </button>
        <ClientMatrix />
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Clients Management</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage clients and track their update status
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowMatrix(true)} className="btn-secondary gap-2">
            <Building2 className="h-4 w-4" />
            View Matrix
          </button>
          <button onClick={() => setShowAddModal(true)} className="btn-primary gap-2">
            <Plus className="h-4 w-4" />
            Add Client
          </button>
        </div>
      </div>

      {/* Clients Grid */}
      {clients.length === 0 ? (
        <div className="card">
          <EmptyState
            icon={Users}
            title="No clients yet"
            description="Add your first client to start tracking their update status."
            action={
              <button onClick={() => setShowAddModal(true)} className="btn-primary gap-2">
                <Plus className="h-4 w-4" />
                Add your first client
              </button>
            }
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {clients.map((client) => (
            <div
              key={client.id}
              className="card flex flex-col p-6 transition-shadow hover:shadow-modal"
            >
              <div className="mb-4 flex items-start justify-between gap-3">
                <h3 className="text-lg font-semibold text-foreground">{client.name}</h3>
                <span
                  className={cn(
                    'shrink-0 gap-1.5',
                    client.isActive ? 'badge-success' : 'badge-neutral'
                  )}
                >
                  <span
                    className={cn(
                      'h-1.5 w-1.5 rounded-full',
                      client.isActive ? 'bg-success' : 'bg-muted-foreground'
                    )}
                  />
                  {client.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>

              {client.description && (
                <p className="mb-4 text-sm text-muted-foreground">{client.description}</p>
              )}

              <div className="space-y-2 text-sm">
                {client.contactPerson && (
                  <div className="flex items-center gap-2 text-foreground">
                    <User className="h-4 w-4 text-muted-foreground" />
                    {client.contactPerson}
                  </div>
                )}
                {client.contactEmail && (
                  <div className="flex items-center gap-2 text-foreground">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <a
                      href={`mailto:${client.contactEmail}`}
                      className="text-primary hover:underline"
                    >
                      {client.contactEmail}
                    </a>
                  </div>
                )}
              </div>

              <div className="mt-4 space-y-2 border-t border-border pt-4">
                <button
                  onClick={() => toggleClientStatus(client)}
                  className={cn(
                    'w-full',
                    client.isActive ? 'btn-outline' : 'btn-secondary'
                  )}
                >
                  {client.isActive ? 'Deactivate' : 'Activate'}
                </button>
                {isAdmin && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => startEdit(client)}
                      className="btn-secondary btn-sm flex-1 gap-1.5"
                      aria-label={`Edit ${client.name}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteClient(client)}
                      disabled={saving}
                      className="btn-outline btn-sm flex-1 gap-1.5 text-destructive"
                      aria-label={`Remove ${client.name}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Remove
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Client Modal (SC-239) */}
      {editingClient && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-secondary-950/50 p-4 backdrop-blur-xs">
          <div
            className="surface-overlay w-full max-w-md animate-slide-up p-6 shadow-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Edit client"
          >
            <h2 className="mb-4 text-xl font-bold text-foreground">Edit Client</h2>

            <div className="space-y-4">
              <div>
                <label className="label" htmlFor="edit-client-name">
                  Client Name *
                </label>
                <input
                  id="edit-client-name"
                  type="text"
                  value={editDraft.name}
                  onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })}
                  className="input"
                />
              </div>
              <div>
                <label className="label" htmlFor="edit-client-description">
                  Description
                </label>
                <textarea
                  id="edit-client-description"
                  value={editDraft.description}
                  onChange={(e) => setEditDraft({ ...editDraft, description: e.target.value })}
                  className="input"
                  rows={3}
                />
              </div>
              <div>
                <label className="label" htmlFor="edit-client-person">
                  Contact Person
                </label>
                <input
                  id="edit-client-person"
                  type="text"
                  value={editDraft.contactPerson}
                  onChange={(e) => setEditDraft({ ...editDraft, contactPerson: e.target.value })}
                  className="input"
                />
              </div>
              <div>
                <label className="label" htmlFor="edit-client-email">
                  Contact Email
                </label>
                <input
                  id="edit-client-email"
                  type="email"
                  value={editDraft.contactEmail}
                  onChange={(e) => setEditDraft({ ...editDraft, contactEmail: e.target.value })}
                  className="input"
                />
              </div>
            </div>

            <div className="mt-6 flex gap-2">
              <button onClick={cancelEdit} className="btn-secondary flex-1">
                Cancel
              </button>
              <button onClick={handleUpdateClient} disabled={saving} className="btn-primary flex-1">
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Client Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-secondary-950/50 p-4 backdrop-blur-xs">
          <div className="surface-overlay w-full max-w-md animate-slide-up p-6 shadow-modal">
            <h2 className="mb-4 text-xl font-bold text-foreground">Add New Client</h2>

            <div className="space-y-4">
              <div>
                <label className="label" htmlFor="client-name">
                  Client Name *
                </label>
                <input
                  id="client-name"
                  type="text"
                  value={newClient.name}
                  onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                  className="input"
                  placeholder="Enter client name"
                />
              </div>

              <div>
                <label className="label" htmlFor="client-description">
                  Description
                </label>
                <textarea
                  id="client-description"
                  value={newClient.description}
                  onChange={(e) => setNewClient({ ...newClient, description: e.target.value })}
                  className="input resize-none"
                  rows={3}
                  placeholder="Enter description"
                />
              </div>

              <div>
                <label className="label" htmlFor="client-contact-person">
                  Contact Person
                </label>
                <input
                  id="client-contact-person"
                  type="text"
                  value={newClient.contactPerson}
                  onChange={(e) => setNewClient({ ...newClient, contactPerson: e.target.value })}
                  className="input"
                  placeholder="Enter contact person"
                />
              </div>

              <div>
                <label className="label" htmlFor="client-contact-email">
                  Contact Email
                </label>
                <input
                  id="client-contact-email"
                  type="email"
                  value={newClient.contactEmail}
                  onChange={(e) => setNewClient({ ...newClient, contactEmail: e.target.value })}
                  className="input"
                  placeholder="Enter email"
                />
              </div>
            </div>

            <div className="mt-6 flex gap-2">
              <button onClick={() => setShowAddModal(false)} className="btn-outline flex-1">
                Cancel
              </button>
              <button onClick={handleAddClient} className="btn-primary flex-1">
                Add Client
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
